// src/features/cart/CartContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  fetchCart,
  addToCart,
  removeFromCart,
  updateCartItem,
  clearCart,
} from "./cartService";
import { useAuth } from "../../hooks/useAuth";

const CartContext = createContext();
const GUEST_CART_KEY = "guestCart";

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);

  // ===============================
  // 🔹 Guest Cart Helpers
  // ===============================
  const loadGuestCart = useCallback(() => {
    const stored = JSON.parse(localStorage.getItem(GUEST_CART_KEY)) || [];
    const normalized = stored.map(item => ({
      ...item,
      productId: item.productId || item.id,
    }));
    setCart(normalized);
    setLoading(false);
  }, []); // no dependencies – only uses localStorage and setCart

  const saveGuestCart = useCallback((items) => {
    const normalized = items.map(item => ({
      ...item,
      productId: item.productId || item.id,
    }));
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(normalized));
    setCart(normalized);
  }, []); // stable

  // ===============================
  // 🔹 Server Cart Helpers
  // ===============================
  const loadServerCart = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const items = await fetchCart(user.id);
      setCart(items);
    } catch (err) {
      console.error("Failed to load cart:", err);
      setCart([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]); // depends on user.id

  // ===============================
  // 🔹 Add to Cart
  // ===============================
  const handleAddToCart = useCallback(async (item) => {
    // Guest
    if (!user?.id) {
      const existing = JSON.parse(localStorage.getItem(GUEST_CART_KEY)) || [];
      const normalizedExisting = existing.map(i => ({
        ...i,
        productId: i.productId || i.id,
      }));
      const existingItem = normalizedExisting.find(i => i.productId === item.id);
      let updated;
      if (existingItem) {
        updated = normalizedExisting.map(i =>
          i.productId === item.id
            ? { ...i, quantity: (i.quantity || 1) + 1 }
            : i
        );
      } else {
        updated = [
          ...normalizedExisting,
          { ...item, productId: item.id, quantity: 1 },
        ];
      }
      saveGuestCart(updated);
      return;
    }

    // Logged in
    try {
      await addToCart({
        productId: item.id,
        userId: user.id,
        quantity: 1,
      });
      await loadServerCart();
    } catch (err) {
      console.error("Add to cart failed:", err);
    }
  }, [user?.id, saveGuestCart, loadServerCart]); // depends on user, guest helpers, server loader

  // ===============================
  // 🔹 Merge Guest Cart → Server
  // ===============================
  const mergeGuestCart = useCallback(async () => {
    const guestCart = JSON.parse(localStorage.getItem(GUEST_CART_KEY)) || [];
    if (!guestCart.length || !user?.id) return;

    try {
      for (const item of guestCart) {
        const productId = item.productId || item.id;
        await addToCart({
          productId,
          quantity: item.quantity || 1,
          userId: user.id,
        });
      }
      localStorage.removeItem(GUEST_CART_KEY);
      // After merging, load the updated server cart
      await loadServerCart();
    } catch (err) {
      console.error("Failed to merge guest cart:", err);
    }
  }, [user?.id, loadServerCart]); // depends on user and loadServerCart (addToCart is stable from service)

  // ===============================
  // 🔹 Remove From Cart
  // ===============================
  const handleRemoveFromCart = useCallback(async (productId) => {
    if (!user?.id) {
      const existing = JSON.parse(localStorage.getItem(GUEST_CART_KEY)) || [];
      const normalized = existing.map(i => ({
        ...i,
        productId: i.productId || i.id,
      }));
      const updated = normalized.filter(item => item.productId !== productId);
      saveGuestCart(updated);
      return;
    }

    try {
      await removeFromCart(user.id, productId);
      await loadServerCart();
    } catch (err) {
      console.error("Remove from cart failed:", err);
    }
  }, [user?.id, saveGuestCart, loadServerCart]);

  // ===============================
  // 🔹 Update Quantity
  // ===============================
  const handleUpdateCartItem = useCallback(async (productId, quantity) => {
    if (!user?.id) {
      const existing = JSON.parse(localStorage.getItem(GUEST_CART_KEY)) || [];
      const normalized = existing.map(i => ({
        ...i,
        productId: i.productId || i.id,
      }));
      const updated = normalized.map(item =>
        item.productId === productId ? { ...item, quantity } : item
      );
      saveGuestCart(updated);
      return;
    }

    try {
      await updateCartItem(user.id, productId, quantity);
      await loadServerCart();
    } catch (err) {
      console.error("Update cart item failed:", err);
    }
  }, [user?.id, saveGuestCart, loadServerCart]);

  // ===============================
  // 🔹 Clear Cart
  // ===============================
  const handleClearCart = useCallback(async () => {
    if (!user?.id) {
      localStorage.removeItem(GUEST_CART_KEY);
      setCart([]);
      return;
    }

    try {
      await clearCart(user.id);
      setCart([]);
    } catch (err) {
      console.error("Clear cart failed:", err);
    }
  }, [user?.id]); // only needs user.id

  // ===============================
  // 🔹 Effect: load cart on user change
  // ===============================
  useEffect(() => {
    if (user?.id) {
      // When user logs in, merge guest cart then load server cart
      mergeGuestCart();
      loadServerCart();
    } else {
      // No user → load guest cart
      loadGuestCart();
    }
  }, [user, mergeGuestCart, loadServerCart, loadGuestCart]);
  // All functions are memoized with useCallback, so they are stable unless their dependencies change.
  // user is already included; when user changes, all functions that depend on user will also change,
  // but that's intentional because we want to re-run the effect.

  // ===============================
  // 🔹 Provide context
  // ===============================
  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart: handleAddToCart,
        removeFromCart: handleRemoveFromCart,
        updateCartItem: handleUpdateCartItem,
        clearCart: handleClearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);